/**
 * Club "effective member" state.
 *
 * The server-side `customer.metafields.custom.clubmember` flag only flips after
 * the backend sync catches up (post-checkout + Comax). In the meantime — while
 * the club-membership product is already in the cart — every join CTA in the
 * theme still asks the customer to join a club they are, for all practical
 * purposes, already joining.
 *
 * This module fills that gap: it computes an "effective member" flag from the
 * server truth OR the current cart, and applies it as `body.is-club-member--effective`.
 * Every join surface hides via `.js-hide-when-club-member` while the flag is on,
 * and the club-benefits banner date pills get rewritten with client-computed
 * join / validity dates until the server-side values are in.
 */
(function () {
  var body = document.body;
  if (!body) return;

  var VARIANT_ID = String(body.getAttribute('data-club-variant-id') || '').trim();
  var PRODUCT_ID = String(body.getAttribute('data-club-product-id') || '').trim();
  var SERVER_FLAG = (body.getAttribute('data-isClubMember') || '').trim() === 'true';
  // An expired membership is not an effective one — the theme deliberately shows
  // those customers a renewal CTA (same popup, renewal wording), so treating the
  // stale `clubmember` flag as "already a member" would hide and block it.
  var SERVER_VALID = (body.getAttribute('data-club-valid-member') || '').trim() === 'true';
  var SERVER_MEMBER = SERVER_FLAG && SERVER_VALID;
  var COMMIT_KEY = 'club_join_committed_at';
  // Two-stage post-checkout signal — the customer clicks checkout while the
  // club variant is in the cart (pending), then when the cart later comes
  // back empty we promote to a confirmed renewal timestamp. Persists across
  // the Shopify-hosted thank-you page redirect via localStorage.
  var PENDING_KEY = 'club_checkout_pending_at';
  var RENEWED_KEY = 'club_renewed_at';
  var PENDING_WINDOW_MS = 30 * 60 * 1000;      // 30 min from click to return
  var RENEWED_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 h backend-sync bridge
  var VALIDITY_MONTHS = 24;

  function cartHasClub(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    if (!VARIANT_ID && !PRODUCT_ID) return false;
    // Match by product id first so every membership variant (1-year,
    // 2-year …) trips the effective flag, not just the first-available
    // variant that Liquid picks for VARIANT_ID.
    return cart.items.some(function (i) {
      if (PRODUCT_ID && String(i.product_id) === PRODUCT_ID) return true;
      if (VARIANT_ID && String(i.variant_id) === VARIANT_ID) return true;
      return false;
    });
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function formatDMY(d) { return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear(); }
  function formatDMYSlash(d) { return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear(); }
  function addMonths(d, months) {
    var out = new Date(d.getTime());
    var day = out.getDate();
    out.setMonth(out.getMonth() + months);
    // Guard against Jan-31 + 1 month rolling to Mar-3, etc.
    if (out.getDate() < day) out.setDate(0);
    return out;
  }

  function getCommitTs() {
    try {
      var v = sessionStorage.getItem(COMMIT_KEY);
      return v ? Number(v) : 0;
    } catch (e) { return 0; }
  }
  function setCommitTs(ts) {
    try { sessionStorage.setItem(COMMIT_KEY, String(ts)); } catch (e) {}
  }
  function clearCommitTs() {
    try { sessionStorage.removeItem(COMMIT_KEY); } catch (e) {}
  }

  function getLS(key) {
    try {
      var v = localStorage.getItem(key);
      return v ? Number(v) : 0;
    } catch (e) { return 0; }
  }
  function setLS(key, ts) {
    try { localStorage.setItem(key, String(ts)); } catch (e) {}
  }
  function clearLS(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  // Public helpers so loyalty-strip-override.js can read the same state without
  // re-implementing the storage protocol. Read-only from outside.
  window.__clubRenewedAt = function () {
    var t = getLS(RENEWED_KEY);
    return t && (Date.now() - t) < RENEWED_WINDOW_MS ? t : 0;
  };
  window.__clubRenewedClear = function () { clearLS(RENEWED_KEY); };

  // Capture the Liquid-rendered value the first time we touch an element, so
  // we can put it back verbatim when the overlay condition falls away (e.g.
  // customer removes the club product from the cart mid-session).
  function stashOriginal(el) {
    if (el.getAttribute('data-club-stat-original') === null) {
      el.setAttribute('data-club-stat-original', el.textContent);
    }
  }
  function overlay(selector, value) {
    var els = document.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) {
      stashOriginal(els[i]);
      els[i].textContent = value;
    }
  }
  function restore(selector) {
    var els = document.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) {
      var original = els[i].getAttribute('data-club-stat-original');
      if (original !== null) els[i].textContent = original;
    }
  }
  function updateDateStats(joinDate, validityDate) {
    overlay('[data-club-stat="join"]', formatDMY(joinDate));
    overlay('[data-club-stat="validity"]', formatDMY(validityDate));
    overlay('[data-club-stat="join-slash"]', formatDMYSlash(joinDate));
    overlay('[data-club-stat="validity-slash"]', formatDMYSlash(validityDate));
  }
  function restoreDateStats() {
    restore('[data-club-stat="join"]');
    restore('[data-club-stat="validity"]');
    restore('[data-club-stat="join-slash"]');
    restore('[data-club-stat="validity-slash"]');
  }

  var lastEffective = null;
  // undefined = not yet observed; the first apply() after page load will not
  // fire the "cart just emptied" branch. That matters for a page load right
  // after checkout: the pending-checkout stamp is fresh and the cart is
  // already empty — the two together are exactly the confirmation we need,
  // so we promote directly on that first tick.
  var lastHadClub = undefined;

  function isPendingCheckoutFresh() {
    var t = getLS(PENDING_KEY);
    return t && (Date.now() - t) < PENDING_WINDOW_MS;
  }
  function isRenewedFresh() {
    var t = getLS(RENEWED_KEY);
    return t && (Date.now() - t) < RENEWED_WINDOW_MS;
  }

  function apply(cart) {
    var hasClub = cartHasClub(cart);

    // Two-stage post-checkout detection. Stage 1 (PENDING_KEY) is set when the
    // customer clicks checkout with the club variant in cart — see click hook
    // below. Stage 2 (RENEWED_KEY) promotes to a confirmed renewal when we
    // either observe a live "had club -> empty" transition, or land on a fresh
    // page load with pending stamp set and the cart already empty (the
    // classic post-checkout return path). Clicking checkout then abandoning
    // leaves only the pending stamp, which expires in 30 min.
    var justEmptied = (lastHadClub === true && !hasClub);
    var firstLoadEmpty = (lastHadClub === undefined && !hasClub);
    if ((justEmptied || firstLoadEmpty) && isPendingCheckoutFresh()) {
      setLS(RENEWED_KEY, Date.now());
      clearLS(PENDING_KEY);
      // Nudge the loyalty strip (and anything else listening) so the bridge
      // flag is picked up on this tick rather than waiting for the next
      // cart mutation.
      try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}
    }
    lastHadClub = hasClub;

    // Effective member = server truth OR club in cart right now OR we're
    // inside the 24 h post-checkout bridge before the backend has synced.
    var effective = SERVER_MEMBER || hasClub || isRenewedFresh();

    // Stamp the moment the cart transitions to holding the club variant — that
    // covers both fresh joins (server=false) and renewals (server=true). If the
    // item is removed we clear the stamp so a future re-add restarts from
    // "today" rather than resurrecting a stale timestamp.
    if (hasClub && !getCommitTs()) setCommitTs(Date.now());
    if (!hasClub && getCommitTs()) clearCommitTs();

    if (effective) body.classList.add('is-club-member--effective');
    else body.classList.remove('is-club-member--effective');

    // Overlay dates whenever the customer added the club variant in this
    // session — Liquid-rendered dates lag until the backend sync commits, so
    // trusting them during that window shows the old renewal/join date. Also
    // overlay during the post-checkout bridge (cart empty but renewal
    // freshly committed) so the personal-area pills already reflect the
    // fresh state even before the backend catches up.
    var stamp = getCommitTs() || getLS(RENEWED_KEY);
    if ((hasClub || isRenewedFresh()) && stamp) {
      var joinDate = new Date(stamp);
      var validityDate = addMonths(joinDate, VALIDITY_MONTHS);
      updateDateStats(joinDate, validityDate);
    } else {
      restoreDateStats();
    }

    if (lastEffective !== effective) {
      lastEffective = effective;
      try {
        document.dispatchEvent(new CustomEvent('club:effective-state', {
          detail: { effective: effective, hasClubInCart: hasClub, serverMember: SERVER_MEMBER }
        }));
      } catch (e) {}
    }
  }

  function fetchAndApply() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(apply)
      .catch(function () {});
  }

  var pending = null;
  function refresh() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () { pending = null; fetchAndApply(); }, 60);
  }

  // Dawn-style pub/sub is the primary hook; fall back to DOM events other
  // parts of the theme dispatch on cart mutations.
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }
  } catch (e) {}
  ['cart:updated', 'cart:refresh', 'cart:change', 'cart-drawer:open'].forEach(function (ev) {
    document.addEventListener(ev, refresh);
  });

  // Guard against direct .club-member-popup-main opens leaking through when
  // the CTAs were already hidden by CSS (belt-and-suspenders).
  //
  // Only a live server-side member is blocked. Having the membership in the cart
  // is not enough: a non-member still needs the popup to submit their details
  // (it refuses the duplicate cart add itself), and blocking on the cart alone
  // turned the join / renewal CTA into a dead button.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.clubmember-popup-trigger, .ragister-account-club, .ragister-main, .ragister-main-page')) {
      if (SERVER_MEMBER) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, true);

  // Stamp "checkout intent" when the customer clicks any element that leads
  // to Shopify's hosted checkout AND the club variant is currently in cart.
  // The apply() cart-transition watcher promotes this to a confirmed
  // RENEWED_KEY once the cart comes back empty (which only happens after a
  // successful checkout, not manual removal).
  function isCheckoutTrigger(el) {
    if (!el || !el.closest) return false;
    if (el.closest('[name="checkout"]')) return true;
    if (el.closest('a[href*="/checkout"]')) return true;
    var form = el.closest('form[action*="/checkout"]');
    if (form) return true;
    // Dawn ships a cart-drawer submit button without name="checkout"; catch it
    // via the form that owns the cart items and posts to /cart.
    var submitInCartForm = el.closest('form[action="/cart"] button[type="submit"]');
    return !!submitInCartForm;
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!isCheckoutTrigger(t)) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cartHasClub(cart)) setLS(PENDING_KEY, Date.now());
      })
      .catch(function () {});
  }, true);

  fetchAndApply();
})();
